import {
  contrastRatio,
  type FlyerIcon,
  type ProductFlyer,
  type StoreCorners,
} from '@avrahami/contracts';
import type { FlyerType } from './fonts';
import type { PhotoGround } from './ground';
import { CONTACT_PATHS, FLYER_ICON_PATHS } from './icons';
import {
  fitLead,
  fitText,
  flyerPrice,
  type FitOptions,
  type FittedText,
  type Measure,
} from './layout';

/**
 * What every template's drawing shares (docs/FLYERS.md, adr/0053): the page, the type, text that
 * shrinks to its box, icons, the logos, prices and the product's photo. A template is a function
 * of a `Page` that draws a whole flyer of `FLYER_SIZE` square.
 */

export const FLYER_SIZE = 1080;
export const W = FLYER_SIZE;

/** An image ready to draw, with its own size. */
export interface FlyerImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  /** A product's photo only (`standPhoto`); without it the photo is blended on its ground. */
  ground?: PhotoGround;
}

export interface FlyerImages {
  product: FlyerImage | null;
  /** The store's logo. */
  logo: FlyerImage | null;
  /** The manufacturer logo the product carries, when its page shows one. */
  maker: FlyerImage | null;
}

/** What a template draws from. */
export interface Page {
  ctx: CanvasRenderingContext2D;
  flyer: ProductFlyer;
  type: FlyerType;
  images: FlyerImages;
}

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

const FALLBACK = 'Assistant, Arial, sans-serif';

export const font = (family: string, weight: number, size: number) =>
  `${String(weight)} ${String(size)}px '${family}', ${FALLBACK}`;
export const bodyFont = ({ type }: Page, weight: number, size: number) =>
  font(type.body, weight, size);
export const displayFont = ({ type }: Page, size: number) =>
  font(type.display, type.displayWeight, size * type.displayScale);

export function measureBody(page: Page): Measure {
  const { ctx } = page;
  return (text, size, weight) => {
    ctx.font = bodyFont(page, weight, size);
    return ctx.measureText(text).width;
  };
}

export function measureDisplay(page: Page): Measure {
  const { ctx } = page;
  return (text, size) => {
    ctx.font = displayFont(page, size);
    return ctx.measureText(text).width;
  };
}

/** The corners of a template's frames in the store's choice, at most `largest`. */
export function cornerOf(corners: StoreCorners, largest: number): number {
  return corners === 'SHARP' ? 0 : corners === 'SOFT' ? Math.min(largest, 16) : largest;
}

/** `wanted` where it reads on `ground`, else the first of `others` that does. */
export function readable(
  wanted: string,
  ground: string,
  others: readonly string[],
  least = 3,
): string {
  for (const color of [wanted, ...others]) {
    if (contrastRatio(color, ground) >= least) return color;
  }
  return contrastRatio('#111111', ground) >= contrastRatio('#ffffff', ground)
    ? '#111111'
    : '#ffffff';
}

export function rect(ctx: CanvasRenderingContext2D, box: Box, radius = 0) {
  ctx.beginPath();
  ctx.roundRect(box.x, box.y, box.w, box.h, radius);
}

export function fillRect(
  ctx: CanvasRenderingContext2D,
  box: Box,
  color: string | CanvasGradient,
  radius = 0,
) {
  ctx.fillStyle = color;
  rect(ctx, box, radius);
  ctx.fill();
}

export function strokeRect(
  ctx: CanvasRenderingContext2D,
  box: Box,
  color: string,
  width: number,
  radius = 0,
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  rect(
    ctx,
    { x: box.x + width / 2, y: box.y + width / 2, w: box.w - width, h: box.h - width },
    radius,
  );
  ctx.stroke();
}

export function line(
  ctx: CanvasRenderingContext2D,
  from: [number, number],
  to: [number, number],
  color: string,
  width = 1,
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(...from);
  ctx.lineTo(...to);
  ctx.stroke();
}

/** Fits `text` to a box in the display or the body type. */
export function fit(
  page: Page,
  text: string,
  kind: 'display' | 'body',
  options: FitOptions,
): FittedText {
  return fitText(text, options, kind === 'display' ? measureDisplay(page) : measureBody(page));
}

/** The description's whole opening sentences in a box, or null (`fitLead`). */
export function fitDescription(
  page: Page,
  description: string,
  options: FitOptions,
): FittedText | null {
  return fitLead(description, options, measureBody(page));
}

interface LinesOptions {
  /** The right edge (or the centre, or the left edge, by `align`). */
  x: number;
  /** The top of the first line. */
  y: number;
  font: string;
  color: string;
  align?: CanvasTextAlign;
  lineHeight?: number;
  direction?: CanvasDirection;
}

/** Fitted lines from the top `y`; answers their height. */
export function drawLines(
  ctx: CanvasRenderingContext2D,
  fitted: FittedText,
  options: LinesOptions,
): number {
  const lineHeight = fitted.size * (options.lineHeight ?? 1.15);
  ctx.save();
  ctx.direction = options.direction ?? 'rtl';
  ctx.textAlign = options.align ?? 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = options.color;
  ctx.font = options.font;
  fitted.lines.forEach((text, index) => {
    ctx.fillText(text, options.x, options.y + fitted.size * 0.92 + index * lineHeight);
  });
  ctx.restore();
  return fitted.lines.length * lineHeight;
}

/** One line of text on a baseline. */
export function drawText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  baseline: number,
  options: {
    font: string;
    color: string;
    align?: CanvasTextAlign;
    direction?: CanvasDirection;
    spacing?: number;
    /** The line shrinks until it is no wider than this. */
    maxWidth?: number;
  },
): number {
  ctx.save();
  ctx.direction = options.direction ?? 'rtl';
  ctx.textAlign = options.align ?? 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.font = options.font;
  ctx.fillStyle = options.color;
  if (options.spacing !== undefined) ctx.letterSpacing = `${String(options.spacing)}px`;
  const { maxWidth } = options;
  if (maxWidth !== undefined) {
    const size = Number.parseFloat(/([\d.]+)px/.exec(options.font)?.[1] ?? '0');
    const width = ctx.measureText(text).width;
    if (size > 0 && width > maxWidth) {
      ctx.font = options.font.replace(
        /[\d.]+px/,
        `${String(Math.floor((size * maxWidth) / width))}px`,
      );
    }
  }
  ctx.fillText(text, x, baseline);
  const width = ctx.measureText(text).width;
  ctx.restore();
  return width;
}

/** A line shortened with an ellipsis until it is no wider than `maxWidth` in `font`. */
export function clip(
  ctx: CanvasRenderingContext2D,
  text: string,
  fontValue: string,
  maxWidth: number,
) {
  ctx.save();
  ctx.font = fontValue;
  let value = text;
  while (value.length > 1 && ctx.measureText(value).width > maxWidth) {
    value = `${value.slice(0, -2).trimEnd()}…`;
  }
  ctx.restore();
  return value;
}

/** One of the line icons, centred on (cx, cy), `size` pixels across. */
export function drawIcon(
  ctx: CanvasRenderingContext2D,
  paths: readonly string[],
  cx: number,
  cy: number,
  size: number,
  color: string,
  weight = 2,
) {
  const scale = size / 24;
  ctx.save();
  ctx.translate(cx - 12 * scale, cy - 12 * scale);
  ctx.scale(scale, scale);
  ctx.strokeStyle = color;
  ctx.lineWidth = weight;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const path of paths) ctx.stroke(new Path2D(path));
  ctx.restore();
}

export const benefitIcon = (icon: FlyerIcon) => FLYER_ICON_PATHS[icon];
export const WHATSAPP_ICON = CONTACT_PATHS.WHATSAPP;
export const PHONE_ICON = CONTACT_PATHS.PHONE;

/** An arrow pointing on (to the left, as Hebrew reads), centred on (cx, cy). */
export function drawArrow(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  length: number,
  color: string,
  weight = 3,
) {
  const head = Math.min(10, length * 0.35);
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = weight;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(cx + length / 2, cy);
  ctx.lineTo(cx - length / 2, cy);
  ctx.moveTo(cx - length / 2 + head, cy - head);
  ctx.lineTo(cx - length / 2, cy);
  ctx.lineTo(cx - length / 2 + head, cy + head);
  ctx.stroke();
  ctx.restore();
}

/** The largest size of `image` that fits the box, centred in it. */
export function contain(image: { width: number; height: number }, box: Box): Box {
  const scale = Math.min(box.w / image.width, box.h / image.height);
  const w = image.width * scale;
  const h = image.height * scale;
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}

/**
 * A logo in a box: the image (on a white ground it sits on the page, not in a white box), or
 * the name in the display type when there is no image. `align` is the side it keeps to.
 */
export function drawLogo(
  page: Page,
  image: FlyerImage | null,
  name: string | null,
  box: Box,
  options: { align: 'right' | 'left' | 'center'; color: string },
) {
  const { ctx } = page;
  if (image !== null) {
    const fitted = contain(image, box);
    const x =
      options.align === 'right'
        ? box.x + box.w - fitted.w
        : options.align === 'left'
          ? box.x
          : fitted.x;
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(image.source, x, fitted.y, fitted.w, fitted.h);
    ctx.restore();
    return;
  }
  if (name === null) return;
  const fitted = fit(page, name, 'display', {
    maxWidth: box.w,
    maxLines: 1,
    maxSize: Math.min(64, box.h * 0.8),
    minSize: 24,
    weight: 800,
  });
  const x =
    options.align === 'right'
      ? box.x + box.w
      : options.align === 'left'
        ? box.x
        : box.x + box.w / 2;
  drawLines(ctx, fitted, {
    x,
    y: box.y + (box.h - fitted.size * 1.1) / 2,
    font: displayFont(page, fitted.size),
    color: options.color,
    align: options.align,
  });
}

/**
 * The product's photo in `area`, changed so the flyer does not carry the catalog's picture
 * (adr/0032): mirrored, turned and recoloured a little, by amounts of this store and product, with
 * the store's name across it faintly. A photo on white is blended on whatever lies under it, and
 * shrinks until the turned photo fits its area, so the product is never cut; a photo with a ground
 * of its own stands in a straight frame, enlarged just enough to keep its corners inside.
 */
export function drawPhoto(page: Page, area: Box, radius = 0) {
  const { ctx, flyer, type } = page;
  const image = page.images.product;
  if (image === null) return;
  const { photo } = flyer.design;
  const framed = image.ground === 'FRAME';
  const box = contain(image, area);
  ctx.save();
  ctx.beginPath();
  if (framed) ctx.roundRect(box.x, box.y, box.w, box.h, radius);
  else ctx.rect(area.x, area.y, area.w, area.h);
  ctx.clip();
  ctx.translate(box.x + box.w / 2, box.y + box.h / 2);
  const turn = ((framed ? photo.rotate * 0.4 : photo.rotate) * Math.PI) / 180;
  ctx.rotate(turn);
  const [cos, sin] = [Math.abs(Math.cos(turn)), Math.abs(Math.sin(turn))];
  const stretch = Math.max(box.w / box.h, box.h / box.w);
  const zoom = framed
    ? cos + stretch * sin
    : Math.min(area.w / (box.w * cos + box.h * sin), area.h / (box.w * sin + box.h * cos));
  ctx.scale(-zoom, zoom);
  ctx.filter = `hue-rotate(${String(photo.hue)}deg) saturate(${String(photo.saturate)})`;
  if (!framed) ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(image.source, -box.w / 2, -box.h / 2, box.w, box.h);
  ctx.restore();

  // The store's name across the photo, faint: the picture is this store's.
  ctx.save();
  ctx.beginPath();
  ctx.rect(area.x, area.y, area.w, area.h);
  ctx.clip();
  ctx.translate(area.x + area.w / 2, area.y + area.h / 2);
  ctx.rotate(-Math.PI / 9);
  ctx.globalAlpha = 0.07;
  ctx.fillStyle = flyer.theme.brandDark;
  ctx.direction = 'rtl';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = font(type.body, 700, 34);
  const name = flyer.store.name.slice(0, 40);
  for (const row of [-1, 0, 1]) ctx.fillText(name, 0, row * Math.max(120, area.h / 3));
  ctx.restore();
}

/**
 * A price as the examples print it: the figure in the display type and a small shekel sign
 * raised beside it. `x` is the price's right edge, or its centre or left edge by `align`.
 * Answers its width.
 */
export function drawAmount(
  page: Page,
  amountMinor: number,
  options: {
    x: number;
    baseline: number;
    size: number;
    color: string;
    align?: 'right' | 'center' | 'left';
    maxWidth?: number;
  },
): number {
  const { ctx } = page;
  const figure = flyerPrice(amountMinor).replace('₪', '');
  let size = options.size;
  const widthAt = (at: number) => {
    ctx.font = displayFont(page, at);
    const number = ctx.measureText(figure).width;
    ctx.font = displayFont(page, at * 0.42);
    return number + ctx.measureText('₪').width + at * 0.06;
  };
  while (options.maxWidth !== undefined && size > 18 && widthAt(size) > options.maxWidth) size -= 2;
  const width = widthAt(size);
  const align = options.align ?? 'right';
  const left =
    align === 'right' ? options.x - width : align === 'center' ? options.x - width / 2 : options.x;
  ctx.save();
  ctx.direction = 'ltr';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = options.color;
  ctx.font = displayFont(page, size);
  const number = ctx.measureText(figure).width;
  ctx.fillText(figure, left, options.baseline);
  ctx.font = displayFont(page, size * 0.42);
  ctx.fillText('₪', left + number + size * 0.06, options.baseline - size * 0.42);
  ctx.restore();
  return width;
}

/** An old price, struck through; answers its width. */
export function drawStruck(
  page: Page,
  amountMinor: number,
  options: {
    x: number;
    baseline: number;
    font: string;
    color: string;
    strike: string;
    align?: CanvasTextAlign;
  },
): number {
  const { ctx } = page;
  const text = flyerPrice(amountMinor);
  ctx.save();
  ctx.font = options.font;
  const width = ctx.measureText(text).width;
  ctx.restore();
  drawText(ctx, text, options.x, options.baseline, {
    font: options.font,
    color: options.color,
    align: options.align ?? 'right',
    direction: 'ltr',
  });
  const align = options.align ?? 'right';
  const left =
    align === 'right' ? options.x - width : align === 'center' ? options.x - width / 2 : options.x;
  ctx.save();
  ctx.font = options.font;
  const size = Number.parseFloat(/(\d+(?:\.\d+)?)px/.exec(options.font)?.[1] ?? '20');
  ctx.restore();
  line(
    ctx,
    [left - 3, options.baseline - size * 0.32],
    [left + width + 3, options.baseline - size * 0.32],
    options.strike,
    Math.max(2, size / 14),
  );
  return width;
}

/** The store's number to call: its phone, else its WhatsApp. */
export function phoneOf(flyer: ProductFlyer): string | null {
  return flyer.store.phone ?? flyer.store.whatsapp;
}
